import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
// @mui
import { Typography, Box, LinearProgress, Stack, Divider, Link } from '@mui/material';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import Iconify from '../../components/Iconify';
import { ProjectAPI } from '../../services/projects.services';

// ----------------------------------------------------------------------
const COLOR_BLACK = '#000000';
const LINK_COLOR = '#0275B5';
const COLOR_ORANGE = '#FF9E3D';
const COLOR_DARKORANGE = '#D16B06';
const FONT_LATO = '"Lato",sans-serif';
const PESO_SIGN = '\u20B1';

const boxWrapper = {
  width: 1,
  mt: 5,
  minHeight: 450,
  border: 1,
  borderColor: COLOR_ORANGE,
  borderRadius: '5px',
  position: 'relative',
  pt: 11,
  pb: 5,
  px: 5,
};

const donationBox = {
  background: '#FF9E3D',
  width: { xs: 3 / 4, md: 2 / 3 },
  position: 'absolute',
  top: 10,
  left: { xs: 0, md: -25 },
  py: 1.2,
  pl: { xs: 5, md: 8 },
  borderRadius: '0 10px 10px 0',
  color: '#fff',
  fontWeight: 700,
};

const rectangleStyle = {
  width: 0,
  height: 0,
  borderTop: '0px solid transparent',
  borderRight: `25px solid ${COLOR_DARKORANGE}`,
  borderBottom: '25px solid transparent',
  position: 'absolute',
  top: 53,
  left: -25,
};

function BoxContent({ label, value, ...other }) {
  return (
    <Box {...other}>
      <Typography variant={'body1'} fontWeight={500}>
        {label}
      </Typography>
      <Typography variant={'h3'} fontFamily={FONT_LATO} pt={'2px'} color={COLOR_ORANGE}>
        {value}
      </Typography>
    </Box>
  );
}

CharityDonationBox.propTypes = {
  charity: PropTypes.object,  
};

export default function CharityDonationBox({ charity }) {  
  const totalDonations = charity.charityDetails?.totalDonations;
  const address = charity.charityDetails?.address;
  const website = charity.charityDetails?.website;
  const socialMedia  = charity.charityDetails?.socialMedia;     
  const [ongoingProjects, setOngoingProjects] = useState([]);
  const [charityDonations, setCharityDonations] = useState(0);
  const [completedProjects,setCompletedProjects] = useState([]);
  const currency = 'PHP'
  // const totalDonations = '0'
  const mdUp = useResponsive('up', 'md');
  const mapMarkerIcon = 'clarity:map-marker-solid';
  const websiteIcon = 'fa-solid:globe';

  async function getProjects(){
    if(charity && charity.projects){
      const ongoing = charity.projects.filter((p)=> p.isActive === true && p.deleted !== true)
      const donations = ongoing.reduce((donations, project) => donations += project?.donationDetails?.raisedDonation, 0); // eslint-disable-line no-return-assign
      setOngoingProjects(ongoing);
      setCharityDonations(donations.toLocaleString());
    }
  }

  useEffect(()=>{
    getProjects();
  },[])
  
  return (
    <Box sx={boxWrapper}>
      <Typography sx={donationBox} typography={{ xs: 'body1', md: 'h6' }}>
        Donation Summary
      </Typography>
      {mdUp && <div style={rectangleStyle} />}
      <BoxContent label="Total Donation" value={`${currency} ${charityDonations}`} sx={{ width: 1 }} />
      <Stack direction={'row'}>
        <BoxContent label="Ongoing Projects" value={ongoingProjects.length} sx={{ mt: 4, width: 1 / 2 }} />
        <BoxContent label="Completed Projects" value={completedProjects.length} sx={{ mt: 4, width: 1 / 2 }} />
      </Stack>
      <Divider sx={{ my: 3 }} />
      <Stack direction={'row'} alignItems={'center'}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Iconify icon={mapMarkerIcon} sx={{ color: COLOR_ORANGE, width: 35, height: 35, mr: 2 }} />
        </div>
        <Typography variant={'body1'} fontFamily={FONT_LATO} pt={'2px'}>
          {address}
        </Typography>
      </Stack>
      <Stack direction={'row'} alignItems={'center'} sx={{ mt: 2 }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Iconify icon={websiteIcon} sx={{ color: COLOR_ORANGE, width: 30, height: 30, mr: 2, ml: 0.5 }} />
        </div>
        {website ? (
          <Link
            href="#"
            underline="hover"
            target={'_blank'}
            sx={{
              cursor: 'pointer',
              color: COLOR_BLACK,
              fontWeight: '700 !important',
              typography: 'body1',
              '&:hover': {
                color: LINK_COLOR,
              },
            }}
            onClick={() => {
              if (website) {
                window.open(`http://${website}`, '_blank');
              }
            }}
          >
            {website}
          </Link>
        ) : (
          <Link
            href="#"
            underline="hover"
            target={'_blank'}
            sx={{
              cursor: 'pointer',
              color: COLOR_BLACK,
              fontWeight: '700 !important',
              typography: 'body1',
              wordBreak: 'break-word',
              '&:hover': {
                color: LINK_COLOR,
              },
            }}
            onClick={() => {
              if (socialMedia && socialMedia.length > 0) {
                window.open(`http://${socialMedia?.url}`, '_blank');
              }
            }}
          >
            {socialMedia ? socialMedia?.url : ''}
          </Link>
        )}
      </Stack>
    </Box>
  );
}
